import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import qs from 'qs';
import { debounce } from 'lodash';
import moment from 'moment/moment';
import fetchSearchParams from '../utils/fetchSearchParams';
import { SearchState } from '../components/search/admin-dashboard-tables/interfaces';
import { validAgeSelectionOptions } from '../utils/constants';
import { parsePriceAndAgeFromNumericFilters } from '../components/search/utils';

const useURLSync = (searchRefinementsToTrack: string[], syncURL = true) => {
  // TODO: Will need to update this to use new facet format
  const createURL = (searchState) => {
    const {
      query,
      refinementList = {},
      hitsPerPage,
      sortBy,
      configure,
    } = searchState;

    const {
      aroundLatLng = '',
      numericFilters = [],
    } = configure;

    let lat = '';
    let lng = '';
    if (Array.isArray(aroundLatLng)) {
      [lat, lng] = aroundLatLng;
    } else {
      [lat, lng] = aroundLatLng.split(', ');
    }

    const {
      minPrice,
      maxPrice,
      diffInMonths,
    } = parsePriceAndAgeFromNumericFilters(numericFilters);

    const queryParameters = {
      query: query ? encodeURIComponent(query) : '',
      minPrice: minPrice.trim(),
      maxPrice: maxPrice.trim(),
      age: diffInMonths,
      hitsPerPage,
      sortBy,
      lat,
      lng,
    };

    Object.keys(refinementList).forEach((key) => {
      if (Array.isArray(refinementList[key])) {
        queryParameters[key] = `${refinementList[key].join(',').replaceAll(' ', '+')}`;
      } else {
        queryParameters[key] = refinementList[key].replaceAll(' ', '+');
      }
    });

    Object.keys(queryParameters).forEach((key) => {
      if (!queryParameters[key]) {
        delete queryParameters[key];
      }
    });

    /**
     * Logic pertaining to any query params that might be
     * introduced/for a function outside of search
    */
    const urlParams = fetchSearchParams();

    // For Google Ad Tracking
    const googleAdKey = 'gclid';
    const googleAdValue = urlParams[googleAdKey];
    if (googleAdValue) {
      queryParameters[googleAdKey] = googleAdValue;
    }

    const searchQueryString = qs.stringify(queryParameters, { encode: false });

    return searchQueryString && `?${searchQueryString}`;
  };

  const searchStateToUrl = (searchState) => {
    if (searchState) {
      return createURL(searchState);
    }
    return '';
  };

  const urlToSearchState = ({ search }): SearchState => {
    const queryObject = qs.parse(
      search.slice(1),
      { comma: true },
    );

    const {
      hitsPerPage,
      query = '',
      lat = '',
      lng = '',
      minPrice = '',
      maxPrice = '',
      age,
      ...rest
    } = queryObject;

    const refinementList = {};
    if (queryObject) {
      searchRefinementsToTrack.forEach((refinement) => {
        const queriedRefinement = queryObject[refinement];
        if (queriedRefinement) {
          // All refinements should be set in state as arrays
          refinementList[refinement] = Array.isArray(queriedRefinement)
            ? queriedRefinement
            : [queriedRefinement];
          delete rest[refinement];
        }
      });
    }

    let price = '';
    if (minPrice && maxPrice) {
      price = `real_price: ${minPrice} TO ${maxPrice}`;
    } else if (minPrice) {
      price = `real_price >= ${minPrice}`;
    } else if (maxPrice) {
      price = `real_price <= ${maxPrice}`;
    }

    // Since age range options are pre-defined, despite being a numeric filter,
    // we should verify that a legitimate option has been selected
    const validAgeOptions = validAgeSelectionOptions.map((option) => option.value);
    let minAge = '';
    let maxAge = '';
    if (typeof age === 'string' && validAgeOptions.includes(age)) {
      const ageInt = parseInt(age, 10);
      if (ageInt > 1) {
        minAge = moment().utc().subtract(ageInt, 'months').format('X');
        maxAge = moment().utc().format('X');
      } else {
        maxAge = moment().utc().subtract(ageInt, 'years').format('X');
      }
    }

    const newSearchState: SearchState = {
      query: query ? decodeURIComponent(String(query)) : '',
      refinementList,
      configure: {
        aroundLatLng: [lat as string, lng as string],
        numericFilters: [
          ...price ? [price] : [],
        ],
      },
      ...rest,
    };

    if (minAge || maxAge) {
      const minAgeInt = minAge ? parseInt(minAge, 10) : 0;
      const maxAgeInt = parseInt(maxAge, 10);
      const ageQuery = `birth_date:${minAgeInt} TO ${maxAgeInt}`;
      newSearchState.configure?.numericFilters?.push(ageQuery);
    }

    const hitsPPNumber = hitsPerPage
      ? Number(hitsPerPage)
      : undefined;

    if (hitsPPNumber && Number.isInteger(hitsPPNumber)) {
      newSearchState.hitsPerPage = hitsPPNumber;
    }

    return newSearchState;
  };

  const [user] = useState(window.user);
  const [searchState, setSearchState] = useState<SearchState>(urlToSearchState(window.location));
  const onPopState = ({ state }: any) => setSearchState(state || {});

  useEffect(() => {
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const pushSearchState = useRef(
    debounce((newSearchState) => {
      window.history.pushState(
        newSearchState,
        '',
        searchStateToUrl(newSearchState),
      );
    }, 400),
  ).current;
  useEffect(() => {
    return () => {
      pushSearchState.cancel();
    };
  }, [pushSearchState]);

  useEffect(() => {
    if (syncURL) {
      pushSearchState(searchState);
    }
  }, [searchState, syncURL, pushSearchState]);

  const debouncedUpdateSearchStateQuery = useRef(
    debounce((currentSearchState, newQuery) => {
      if (currentSearchState && currentSearchState.query !== newQuery) {
        setSearchState((s) => ({
          ...s,
          query: newQuery,
        }));
      }
    }, 400),
  ).current;
  useEffect(() => {
    return () => {
      debouncedUpdateSearchStateQuery.cancel();
    };
  }, [debouncedUpdateSearchStateQuery]);

  // The code below syncs this to the query in the global search bar
  const updateQuery = useCallback((e: any) => {
    const { detail } = e as { detail: { query: () => string } };
    if (detail && detail.query) {
      const newQuery = detail.query();
      debouncedUpdateSearchStateQuery(searchState, newQuery);
    }
  }, [searchState, debouncedUpdateSearchStateQuery]);

  useEffect(() => {
    document.addEventListener('updateQuery', updateQuery);
    return () => document.removeEventListener('updateQuery', updateQuery);
  }, [updateQuery]);

  /**
   * Resets the searchState page number back to 1.
   * If a newIndexName is provided, it also changes the index/sort.
   *
   * @param {string?} newIndexName
   */
  const resetPagination = useCallback((newIndexName?: string) => {
    setSearchState((currentSearchState) => ({
      ...currentSearchState,
      page: 1,
      sortBy: newIndexName || currentSearchState.sortBy,
    }));
  }, []);

  if (!syncURL) {
    return {
      createURL: () => {},
      resetPagination,
      searchState,
      setSearchState,
      user,
    };
  }

  return {
    createURL: searchStateToUrl,
    resetPagination,
    searchState,
    setSearchState,
    user,
  };
};

export default useURLSync;
